import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, MapPin, Phone, Mail, Building2,
  ArrowRight, Loader2, AlertCircle, Sparkles, ChevronRight, LogIn,
} from "lucide-react";
import { getPalette, renderMarkdown } from "@/lib/landing";
import { applyFonts } from "@/lib/landingFonts";
import SchoolPortalLoginDialog from "@/components/SchoolPortalLoginDialog";
import PublicApplyNowDialog from "@/components/PublicApplyNowDialog";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";
import useSchoolManifest from "@/lib/useSchoolManifest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "S";
}

export default function PublicSchoolLandingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${BACKEND_URL}/api/public/schools/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  // Apply the school's custom fonts (heading / body / button) to the public
  // landing page. Reset on unmount so we don't leak fonts into other pages.
  useEffect(() => {
    if (!data?.school) return;
    applyFonts({
      heading: data.school.font_heading || null,
      body: data.school.font_body || null,
      button: data.school.font_button || null,
    });
    return () => applyFonts({ heading: null, body: null, button: null });
  }, [data?.school?.font_heading, data?.school?.font_body, data?.school?.font_button]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] via-white to-[#DBEAFE]">
        <Loader2 className="w-10 h-10 text-[#2563EB] animate-spin" />
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] via-white to-[#DBEAFE] px-4">
        <div className="max-w-md text-center" data-testid="public-school-404">
          <div className="w-20 h-20 mx-auto rounded-3xl bg-white shadow-floating flex items-center justify-center mb-5">
            <AlertCircle className="w-10 h-10 text-[#E65100]" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-foreground tracking-tight">School not found</h1>
          <p className="text-slate-500 mt-2">The page you're looking for either doesn't exist or this school hasn't been approved yet.</p>
          <Link to="/" className="inline-block mt-6">
            <Button className="rounded-full h-11 px-6 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-floating">
              Back to SchoolBoss <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] via-white to-[#DBEAFE]">
        <p className="text-slate-500">Could not load this page. Please try again.</p>
      </div>
    );
  }

  return <LandingView school={data.school} branches={data.branches} />;
}

/* -------------------------------------------------------------------------- */
/* LandingView — exported so /landing-settings can render a live preview      */
/* -------------------------------------------------------------------------- */
export function LandingView({ school, branches = [], compact = false }) {
  const palette = useMemo(() => getPalette(school?.theme_color), [school?.theme_color]);
  const displayName = school?.public_display_name || school?.name || "School";

  // Per-school PWA — swaps the document manifest + theme-color so installing
  // from this page produces a school-branded home-screen icon.
  useSchoolManifest(school?.slug || "", palette.text);
  const descriptionHTML = useMemo(
    () => renderMarkdown(school?.public_description_md || school?.description || ""),
    [school?.public_description_md, school?.description]
  );
  const [loginOpen, setLoginOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  return (
    <div
      className="min-h-screen text-foreground"
      style={{ background: `linear-gradient(135deg, ${palette.softer}, #FFFFFF, ${palette.soft})` }}
    >
      {/* Top bar */}
      {!compact && (
        <header className="px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="back-to-home">
            {school?.logo_base64 ? (
              <img
                src={school.logo_base64}
                alt={`${school.public_display_name || school.name} logo`}
                className="w-10 h-10 rounded-2xl object-cover shadow-floating bg-white"
                data-testid="school-header-logo"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-2xl flex items-center justify-center shadow-floating"
                style={{ background: `linear-gradient(135deg, ${palette.from}, ${palette.to})` }}
              >
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-heading font-semibold text-lg truncate max-w-[14rem] sm:max-w-xs">
              {school?.public_display_name || school?.name || (
                <>School<span style={{ color: palette.text }}>Boss</span></>
              )}
            </span>
          </Link>
          <div className="flex items-center gap-2">
            {school?.contact_email && (
              <a href={`mailto:${school.contact_email}`} className="hidden sm:inline-block">
                <Button variant="outline" className="rounded-full" style={{ borderColor: `${palette.text}30`, color: palette.text }}>
                  <Mail className="w-4 h-4 mr-1.5" /> Contact us
                </Button>
              </a>
            )}
            <Button
              onClick={() => setLoginOpen(true)}
              className="rounded-full h-10 px-5 text-white font-heading font-semibold shadow-floating hover:shadow-floating-hover transition-all"
              style={{
                background: `linear-gradient(90deg, ${palette.from}, ${palette.to})`,
                boxShadow: `0 8px 20px -10px ${palette.glow}`,
              }}
              data-testid="public-login-btn"
            >
              <LogIn className="w-4 h-4 mr-1.5" /> Login
            </Button>
          </div>
        </header>
      )}

      {/* Hero — banner photo (if uploaded) OR gradient blobs */}
      <section className="relative px-5 sm:px-8 lg:px-12 pt-4 pb-16 overflow-hidden">
        {school?.hero_photo_base64 ? (
          <div className="absolute inset-x-0 top-0 h-72 sm:h-96 overflow-hidden pointer-events-none">
            <img src={school.hero_photo_base64} alt="" className="w-full h-full object-cover" data-testid="public-hero-photo" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-white/40 to-white" />
          </div>
        ) : (
          <>
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-50 blur-3xl pointer-events-none" style={{ background: palette.blobA }} />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full opacity-60 blur-3xl pointer-events-none" style={{ background: palette.blobB }} />
          </>
        )}

        <div className="relative max-w-5xl mx-auto text-center pt-12">
          {/* Logo */}
          <div
            className="mx-auto w-28 h-28 rounded-3xl overflow-hidden shadow-floating ring-4 ring-white flex items-center justify-center mb-7"
            style={{ background: `linear-gradient(135deg, ${palette.soft}, ${palette.blobA})` }}
          >
            {school?.logo_base64
              ? <img src={school.logo_base64} alt={displayName} className="w-full h-full object-cover" data-testid="public-school-logo" />
              : <span className="font-heading text-4xl font-bold" style={{ color: palette.text }}>{initials(displayName)}</span>
            }
          </div>

          <span
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white shadow-sm border font-heading text-sm font-semibold mb-5"
            style={{ borderColor: `${palette.text}25`, color: palette.text }}
          >
            <Sparkles className="w-4 h-4" /> Approved · Verified on SchoolBoss
          </span>

          <h1
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]"
            data-testid="public-school-name"
          >
            {displayName}
          </h1>

          {descriptionHTML && (
            <div
              className="text-lg sm:text-xl text-slate-600 leading-relaxed mt-6 max-w-2xl mx-auto text-left sm:text-center"
              data-testid="public-school-description"
              dangerouslySetInnerHTML={{ __html: descriptionHTML }}
            />
          )}

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => setApplyOpen(true)}
              className="rounded-full h-12 px-7 text-white font-heading font-semibold shadow-floating hover:shadow-floating-hover transition-all"
              style={{
                background: `linear-gradient(90deg, ${palette.from}, ${palette.to})`,
                boxShadow: `0 10px 24px -12px ${palette.glow}`,
              }}
              data-testid="public-apply-now-btn"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Apply Now
            </Button>
            {school?.contact_email && (
              <a href={`mailto:${school.contact_email}`}>
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-6 bg-white/80 backdrop-blur shadow-sm"
                  style={{ borderColor: `${palette.text}30`, color: palette.text }}
                  data-testid="public-inquire-btn"
                >
                  <Mail className="w-4 h-4 mr-1.5" /> Inquire by email
                </Button>
              </a>
            )}
            {school?.contact_phone && (
              <a href={`tel:${school.contact_phone}`}>
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-6 bg-white/80 backdrop-blur shadow-sm"
                  style={{ borderColor: `${palette.text}30`, color: palette.text }}
                  data-testid="public-call-btn"
                >
                  <Phone className="w-4 h-4 mr-1.5" /> {school.contact_phone}
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Install PWA prompt — auto-hides when already installed or unsupported */}
      <section className="px-5 sm:px-8 lg:px-12 -mt-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <InstallPwaPrompt slug={school?.slug} schoolName={displayName} palette={palette} />
        </div>
      </section>

      {/* Branches */}
      <section className="px-5 sm:px-8 lg:px-12 pb-16">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              {branches.length === 0 ? "Branches coming soon" : `${branches.length} ${branches.length === 1 ? "Campus" : "Campuses"}`}
            </h2>
            <p className="text-slate-500 mt-2">
              {branches.length === 0
                ? "We're setting up our first campus. Reach out for details."
                : "Visit any of our approved campuses."}
            </p>
          </div>

          {branches.length > 0 && (
            <div className="flex flex-wrap justify-center gap-5" data-testid="public-branches-grid">
              {branches.map((b) => (
                <div
                  key={b.id}
                  className="bg-white rounded-2xl border shadow-floating card-lift p-6 w-full sm:w-[22rem]"
                  style={{ borderColor: `${palette.text}15` }}
                  data-testid={`public-branch-${b.id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {b.logo_base64 ? (
                      <img src={b.logo_base64} alt={b.name} className="w-12 h-12 rounded-2xl object-cover ring-1" style={{ borderColor: palette.text }} />
                    ) : (
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: palette.soft, color: palette.text }}>
                        <Building2 className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-bold leading-tight break-all">{b.name}</h3>
                    </div>
                  </div>
                  {b.address && (
                    <p className="text-sm text-slate-600 flex items-start gap-1.5 mt-2 break-words">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: palette.text }} />
                      <span className="min-w-0 break-words">{b.address}</span>
                    </p>
                  )}
                  {b.contact_phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-1.5 mt-1.5">
                      <Phone className="w-4 h-4 shrink-0" style={{ color: palette.text }} />
                      <a href={`tel:${b.contact_phone}`} className="hover:underline break-all min-w-0" style={{ color: palette.text }}>{b.contact_phone}</a>
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact strip */}
      <section className="px-5 sm:px-8 lg:px-12 pb-20">
        <div className="max-w-5xl mx-auto bg-white rounded-3xl border shadow-floating p-7 sm:p-10" style={{ borderColor: `${palette.text}20` }}>
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Talk to us</h2>
              <p className="text-slate-500 mt-1">We'd love to hear from you. Reach out by email or phone.</p>
            </div>
            <div className="space-y-2">
              {school?.contact_email && (
                <a href={`mailto:${school.contact_email}`} className="flex items-center gap-2 text-foreground hover:underline font-heading">
                  <Mail className="w-4 h-4" style={{ color: palette.text }} /> {school.contact_email}
                </a>
              )}
              {school?.contact_phone && (
                <a href={`tel:${school.contact_phone}`} className="flex items-center gap-2 text-foreground hover:underline font-heading">
                  <Phone className="w-4 h-4" style={{ color: palette.text }} /> {school.contact_phone}
                </a>
              )}
              {school?.address && (
                <p className="flex items-start gap-2 text-slate-600 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: palette.text }} /> {school.address}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {!compact && (
        <footer className="px-5 sm:px-8 lg:px-12 pb-8 text-center text-xs text-slate-400">
          <p>
            Powered by{" "}
            <Link to="/" className="font-semibold hover:underline" style={{ color: palette.text }}>SchoolBoss</Link>
            <ChevronRight className="inline w-3 h-3 mx-0.5" />
            The all-in-one platform for modern schools.
          </p>
        </footer>
      )}

      {!compact && school?.slug && (
        <SchoolPortalLoginDialog
          open={loginOpen}
          onOpenChange={setLoginOpen}
          slug={school.slug}
          displayName={displayName}
          palette={palette}
          logoBase64={school.logo_base64 || ""}
        />
      )}

      {school?.slug && (
        <PublicApplyNowDialog
          open={applyOpen}
          onOpenChange={setApplyOpen}
          slug={school.slug}
          schoolName={displayName}
          branches={branches}
          palette={palette}
        />
      )}
    </div>
  );
}
